import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useCategories, useBrands } from '../../hooks/useProducts.js';
import { useAuth } from '../../hooks/useAuth.js';
import { apiService, tokenManager } from '../../services/api.js';

const Navigation = () => {
  const { isAuthenticated, store: currentStore, isEmployee, isBrandUser } = useAuth();

  if (!isAuthenticated) return null;

  if (isBrandUser()) {
    return <BrandUserNavigation />;
  }

  return <CustomerNavigation currentStore={currentStore} isEmployee={isEmployee} />;
};

/**
 * Brand user navigation — shows assigned brands as filter buttons
 */
const BrandUserNavigation = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeBrandId = searchParams.get('brandId');

  // Fetch unique brands from the products endpoint (already filtered by API for brand users)
  const { data: brands = [], isLoading } = useQuery({
    queryKey: ['brandUserBrands', tokenManager.getCurrentCompany()?.id],
    queryFn: async () => {
      const companyId = tokenManager.getCurrentCompany()?.id;
      if (!companyId) return [];

      const response = await apiService.get('/storefront-odata/vwStorefrontProductsWithTotals', {
        $filter: `CompanyID eq ${companyId} and OptStatusID eq 1`,
        $select: 'brandID,brandName',
        $orderby: 'brandName',
        $count: true
      });

      const items = response.value || [];
      // Deduplicate brands
      const brandMap = new Map();
      items.forEach(item => {
        if (item.brandID && !brandMap.has(item.brandID)) {
          brandMap.set(item.brandID, { id: item.brandID, name: item.brandName });
        }
      });
      return Array.from(brandMap.values());
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const handleBrandClick = (brandId) => {
    const params = new URLSearchParams(searchParams);
    if (brandId === null) {
      params.delete('brandId');
    } else {
      params.set('brandId', brandId);
    }
    setSearchParams(params, { replace: true });
  };

  // Only show nav if user has multiple brands
  if (isLoading || brands.length <= 1) return null;

  return (
    <nav className="stash-navigation">
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <ul className="stash-navigation__menu">
              <li className="stash-navigation__item">
                <button
                  className={`stash-navigation__link ${!activeBrandId ? 'stash-navigation__link--active' : ''}`}
                  onClick={() => handleBrandClick(null)}
                >
                  All Brands
                </button>
              </li>
              {brands.map((brand) => (
                <li key={brand.id} className="stash-navigation__item">
                  <button
                    className={`stash-navigation__link ${activeBrandId === String(brand.id) ? 'stash-navigation__link--active' : ''}`}
                    onClick={() => handleBrandClick(brand.id)}
                  >
                    {brand.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </nav>
  );
};

/**
 * Standard customer/employee navigation
 */
const CustomerNavigation = ({ currentStore, isEmployee }) => {
  const [activeDropdown, setActiveDropdown] = useState(null);

  const { data: categories = [], isLoading: categoriesLoading } = useCategories();
  const { data: brands = [], isLoading: brandsLoading } = useBrands();

  const handleDropdownToggle = (dropdownName) => {
    setActiveDropdown(activeDropdown === dropdownName ? null : dropdownName);
  };

  const closeDropdown = () => {
    setActiveDropdown(null);
  };

  return (
    <nav className="stash-navigation">
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <ul className="stash-navigation__menu">

              {/* Brands Dropdown */}
              <li
                className={`stash-navigation__item stash-navigation__item--dropdown ${activeDropdown === 'brands' ? 'show' : ''}`}
                onMouseEnter={() => handleDropdownToggle('brands')}
                onMouseLeave={closeDropdown}
              >
                <button
                  className="stash-navigation__link stash-navigation__link--dropdown"
                  onClick={() => handleDropdownToggle('brands')}
                >
                  Brands
                </button>

                <div className={`stash-navigation__dropdown ${activeDropdown === 'brands' ? 'show' : ''}`}>
                  {brandsLoading ? (
                    <div className="stash-navigation__dropdown-loading">
                      <div className="spinner-border spinner-border-sm text-stash-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="stash-navigation__dropdown-scroll">
                        {brands.map((brand) => (
                          <Link
                            key={brand.id}
                            to={`/brands/${brand.slug}`}
                            className="stash-navigation__dropdown-item"
                            onClick={closeDropdown}
                          >
                            {brand.name}
                          </Link>
                        ))}
                      </div>
                      <div className="stash-navigation__dropdown-divider"></div>
                      <Link
                        to="/brands"
                        className="stash-navigation__dropdown-item stash-navigation__dropdown-item--view-all"
                        onClick={closeDropdown}
                      >
                        View All Brands
                      </Link>
                    </>
                  )}
                </div>
              </li>

              {/* Categories Dropdown */}
              <li
                className={`stash-navigation__item stash-navigation__item--dropdown ${activeDropdown === 'categories' ? 'show' : ''}`}
                onMouseEnter={() => handleDropdownToggle('categories')}
                onMouseLeave={closeDropdown}
              >
                <button
                  className="stash-navigation__link stash-navigation__link--dropdown"
                  onClick={() => handleDropdownToggle('categories')}
                >
                  Categories
                </button>

                <div className={`stash-navigation__dropdown ${activeDropdown === 'categories' ? 'show' : ''}`}>
                  {categoriesLoading ? (
                    <div className="stash-navigation__dropdown-loading">
                      <div className="spinner-border spinner-border-sm text-stash-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <>
                      {categories.slice(0, 12).map((category) => (
                        <Link
                          key={category.id}
                          to={`/categories/${category.slug}`}
                          className="stash-navigation__dropdown-item"
                          onClick={closeDropdown}
                        >
                          {category.name}
                        </Link>
                      ))}
                      <div className="stash-navigation__dropdown-divider"></div>
                      <Link
                        to="/categories"
                        className="stash-navigation__dropdown-item stash-navigation__dropdown-item--view-all"
                        onClick={closeDropdown}
                      >
                        View All Categories
                      </Link>
                    </>
                  )}
                </div>
              </li>

              <li className="stash-navigation__item">
                <Link to="/new-arrivals" className="stash-navigation__link">
                  New Arrivals
                </Link>
              </li>
              <li className="stash-navigation__item d-none">
                <Link to="/" className="stash-navigation__link">
                  Back In Stock
                </Link>
              </li>

              {/* Quick Order - hidden for employees */}
              {!isEmployee() && (
                <li className="stash-navigation__item">
                  <Link to="/quick-order" className="stash-navigation__link stash-navigation__link--quick-order">
                    Quick Order
                  </Link>
                </li>
              )}


              {/* Sale Items */}
              <li className="stash-navigation__item ms-3">
                <Link to="/products?backInStock=true" className="stash-navigation__link">
                  Back In Stock
                </Link>
              </li>
                            {/* Sale Items */}
              <li className="stash-navigation__item ms-3">
                <Link to="/products?sale=true" className="stash-navigation__link stash-navigation__link--sale">
                  On Sale
                </Link>
              </li>
              {/* Sales Rep - Moved to Right */}
              {currentStore?.salesRep && (
                <li
                  className={`stash-navigation__item stash-navigation__item--dropdown ms-auto ${activeDropdown === 'salesrep' ? 'show' : ''}`}
                  onMouseEnter={() => handleDropdownToggle('salesrep')}
                  onMouseLeave={closeDropdown}
                >
                  <button
                    className="stash-navigation__link stash-navigation__link--dropdown stash-navigation__link--sales-rep"
                    onClick={() => handleDropdownToggle('salesrep')}
                  >
                    <i className="bi bi-person-badge me-1"></i>
                    Sales Rep: {currentStore.salesRep.name}
                  </button>

                  <div className={`w-100 stash-navigation__dropdown stash-navigation__dropdown--sales-rep ${activeDropdown === 'salesrep' ? 'show' : ''}`}>
                    <div className="stash-navigation__sales-rep-card p-3">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <i className="bi bi-person-badge text-primary fs-4"></i>
                        <div>
                          <small className="text-muted d-block">Your Sales Rep</small>
                          <strong>{currentStore.salesRep.name}</strong>
                        </div>
                      </div>
                      {currentStore.salesRep.email && (
                        <div className="mb-1">
                          <a href={`mailto:${currentStore.salesRep.email}`} className="small text-decoration-none">
                            <i className="bi bi-envelope me-2"></i>
                            {currentStore.salesRep.email}
                          </a>
                        </div>
                      )}
                      {currentStore.salesRep.phone && (
                        <div className="mb-2">
                          <a href={`tel:${currentStore.salesRep.phone}`} className="small text-decoration-none">
                            <i className="bi bi-telephone me-2"></i>
                            {currentStore.salesRep.phone}
                          </a>
                        </div>
                      )}

                    </div>
                  </div>
                </li>
              )}

            </ul>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;